/* The evidence library, the review screen and the import screen share this file, because they share
   a vocabulary: a document row, a state chip, a field row with a confidence. Two files would mean
   two definitions of "flagged" that drift apart, and "flagged" is the thing a reviewer navigates by.
   `Sources.tsx` and `SourcesRegisters.tsx` also borrow `.primary` and `.ghost`, so those two keep
   their names and their shape.

   **Redesigned 2026-10-25 to `docs/design/33-ui-build-spec.md`** (VARIANCE 3, MOTION 2, DENSITY 8).
   What changed is rank, density and rhythm, and nothing else:

   - A document is a **row** with hairlines, not a bordered card: a library of 1,276 of them read as
     a blog because every one was a box. Panels carry the structure; rows inside a panel are
     separated by a rule.
   - The library is a **table in effect** (a column header, fixed columns, tabular figures) while
     staying a list of `article` elements, because `ui-check` and the screen tests read each row as
     one.
   - The caveat is small print: 12px, muted, a capped measure. It is not deleted - spec rule 5 - and
     it is not amber, because a correctly disclosed limit is not a warning.
   - No decorative dots, no uppercase eyebrows. A state is a chip with a word in it.
   - Radii are one system: panels 10px (the shell's), controls 6px, chips full.

   Every colour is a token (ADR-0007, stylelint). */

/* ------------------------------------------------------------------------------------ lists */

.list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.list > li + li {
  border-top: 1px solid var(--line);
}

/* Text that sits in a flush panel, beside a list. A flush panel gives rows the full width, so
   anything that is prose needs its own gutter. */
.pad {
  padding: 10px 13px;
}

.pad > :last-child {
  margin-bottom: 0;
}

/* A document, as a row. `align-items: start` so a long refusal reason grows the row downwards and
   does not drag the state chip to its middle. */
.card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 2px 14px;
  align-items: start;
  padding: 9px 13px;
  background: transparent;
  transition: background-color var(--dur-1) var(--ease-std);
}

.card:hover {
  background: var(--panel-2);
}

.cardMain {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.cardSide {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.name {
  overflow: hidden;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  font-size: var(--t-min);
  color: var(--muted);
  font-variant-numeric: lining-nums tabular-nums;
}

/* The second line of a library row: a size, and the way to the record page. */
.subline {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
  align-items: baseline;
  font-size: var(--t-min);
  color: var(--muted);
  font-variant-numeric: lining-nums tabular-nums;
}

/* The way to a document's record page (F-007). The link is `RecordLink`'s and its words are its
   own, so it is quietened from outside by its test id: twelve hundred rows each carrying a full-ink
   underlined link is the loudest thing on the screen, and it is the least often used. */
.card a[data-testid="record-link"] {
  font-size: var(--t-min);
  color: var(--muted);
  text-decoration: none;
}

.card a[data-testid="record-link"]:hover {
  color: var(--accent);
  text-decoration: underline;
}

/* A refusal, with the way out beside it. The reason is the bad news and the remedy is the way out
   of it; one rule down the left ties them to each other and to the row. */
.refusal {
  display: grid;
  gap: 2px;
  margin-top: 4px;
  padding-left: 9px;
  border-left: 2px solid var(--crit-line);
}

.reason {
  font-size: var(--t-min);
  line-height: 1.45;
  color: var(--crit);
  max-width: 92ch;
}

/* The remedy, deliberately NOT in the critical colour: rendering both in red makes the way out look
   like more of the problem. */
.remedy {
  font-size: var(--t-min);
  line-height: 1.45;
  color: var(--ink-2);
  max-width: 92ch;
}

/* ------------------------------------------------------------------------------- the table */

/* The library: kind, period, state and digest get columns, so a reader can run an eye down one.
   The column header is `aria-hidden` and every cell carries its own words, so nothing depends on a
   header a screen reader would not read. */
.tableHead,
.tableRow {
  grid-template-columns: minmax(0, 1fr) 124px 168px 136px 104px 176px;
}

.tableHead {
  display: grid;
  column-gap: 14px;
  padding: 6px 13px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
  background: var(--panel-2);
  border-bottom: 1px solid var(--line);
  border-top: 1px solid var(--line);
}

/* The cells sit directly in the row's grid at full width, and fold into one line under it when the
   screen is narrow. `display: contents` for a wrapper that holds no semantics of its own. */
.cells {
  display: contents;
}

.cell {
  font-size: var(--t-min);
  color: var(--ink-2);
  font-variant-numeric: lining-nums tabular-nums;
  line-height: 20px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cellDigest {
  font-family: var(--mono);
  color: var(--muted);
}

@media (max-width: 1180px) {
  .tableHead {
    display: none;
  }

  .tableRow {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .tableRow .cells {
    grid-column: 1;
    grid-row: 2;
    display: flex;
    flex-wrap: wrap;
    gap: 2px 12px;
  }

  .tableRow .cardSide {
    grid-column: 2;
    grid-row: 1 / span 2;
    align-self: center;
  }
}

/* ------------------------------------------------------------------------------ state chip */

/* A state is `StatusPill`'s: an icon and a word, toned. This wrapper only places it in a grid cell
   and carries `data-document-state`, so a pill is never stretched to the width of its column. */
.chip {
  display: inline-flex;
  justify-self: start;
  min-width: 0;
}

/* ---------------------------------------------------------------------------------- buttons */

/* One primary per view, then outline, then a quiet one. Every control has hover, focus-visible,
   active and disabled; the focus ring is `:focus-visible` from the global sheet and is never
   removed. */
.primary,
.secondary,
.ghost {
  display: inline-block;
  padding: 5px 11px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background-color var(--dur-1) var(--ease-std),
    border-color var(--dur-1) var(--ease-std),
    color var(--dur-1) var(--ease-std);
}

.primary {
  color: var(--on-fill);
  background: var(--accent);
  border: 1px solid var(--accent);
}

.primary:hover:not(:disabled) {
  background: var(--accent-h);
  border-color: var(--accent-h);
}

.primary:active:not(:disabled) {
  background: var(--accent-h);
}

.primary:disabled {
  border-color: var(--btn-off-bg);
  background: var(--btn-off-bg);
  color: var(--btn-off-ink);
  cursor: not-allowed;
}

/* Opening a recorded document is a read, not a decision, so it is quieter than "Review", which is
   somebody being asked to accept a figure into the inventory. */
.secondary {
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
}

.secondary:hover:not(:disabled) {
  color: var(--accent);
  border-color: var(--accent);
  background: var(--accent-w);
}

.secondary:active:not(:disabled) {
  background: var(--accent-line);
}

.secondary:disabled {
  color: var(--disabled);
  border-color: var(--line);
  cursor: not-allowed;
}

.ghost {
  color: var(--ink-2);
  background: transparent;
  border: 1px solid var(--line-2);
}

.ghost:hover:not(:disabled) {
  color: var(--ink);
  background: var(--panel-2);
  border-color: var(--ink-2);
}

.ghost:active:not(:disabled) {
  background: var(--panel-3);
}

.ghost:disabled {
  color: var(--disabled);
  border-color: var(--line);
  cursor: not-allowed;
}

/* A row's buttons are a little tighter than a form's. */
.card .primary,
.card .secondary {
  padding: 3px 10px;
}

/* A repair, not a decision: a link in appearance, and a real `<button>` because it does something. */
.fixLink {
  padding: 0;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
  background: none;
  border: 0;
  cursor: pointer;
  white-space: nowrap;
}

.fixLink:hover {
  color: var(--accent-h);
}

/* **A button is a target, whatever it looks like** - the same rule as `Screen.module.css`'s. This
   copy had no floor, so the review screen's "Fix it" buttons were 17px tall; the accessibility sweep
   did not see it because it measures after a Tab walk has scrolled them out of their panel (and, until
   stage F1, also counted controls scrolled out of view, which hid them a second time). */
button.fixLink {
  display: inline-flex;
  align-items: center;
  min-height: var(--target-min);
}

@media (max-width: 560px) {
  button.fixLink {
    min-height: var(--target-touch);
  }
}

/* ------------------------------------------------------------------------------------ forms */

.field {
  display: grid;
  gap: 4px;
  margin-bottom: 10px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

.field input,
.field select,
.field textarea {
  padding: 6px 9px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  min-width: 0;
}

.field input:hover,
.field select:hover,
.field textarea:hover {
  border-color: var(--ink-2);
}

.field input:focus-visible,
.field select:focus-visible,
.field textarea:focus-visible {
  border-color: var(--focus);
  outline: 2px solid var(--focus);
  outline-offset: 0;
}

.field input:disabled,
.field select:disabled,
.field textarea:disabled {
  color: var(--disabled);
  background: var(--panel-2);
  border-color: var(--line);
  cursor: not-allowed;
}

/* The file chooser. Its own button is restyled to match the secondary one so the control reads as a
   control, and its filename sits beside it in the muted ink. */
.field input[type="file"] {
  padding: 4px;
  font-size: var(--t-min);
  color: var(--muted);
}

.field input[type="file"]::file-selector-button {
  margin-right: 10px;
  padding: 3px 10px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  background: var(--panel-2);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  cursor: pointer;
}

.field input[type="file"]:hover::file-selector-button {
  color: var(--accent);
  border-color: var(--accent);
}

/* Two or three fields in a row, label above each. Collapses to one column when the panel is
   narrow, which is a property of the panel and not of the viewport. */
.formGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  column-gap: 12px;
}

.formGrid .wide {
  grid-column: 1 / -1;
}

.textarea {
  padding: 8px 10px;
  font-family: var(--mono);
  font-size: var(--t-dense);
  line-height: 1.5;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  resize: vertical;
}

/* Two inputs that are one answer: a quantity and its unit, a period's two ends. Side by side
   because "1200" and "kWh" on separate rows read as two questions. */
.pair {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Two controls side by side where there is room and one above the other where there is not. A date
   input has an intrinsic width its flex item cannot shrink below on every platform (the CI browser's
   is wider than a laptop's), so "shrink to fit" widened the screen's body by 52px at 375px. A basis
   to wrap at, not a minimum to fight, is what lets the row break instead of overflow. */
.pair input {
  min-width: 0;
  flex: 1 1 9rem;
}

/* A row of controls under a form. */
.actions {
  margin: 12px 0 0;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

/* A single opt-in beside a field, for a question most documents do not raise: "this statement
   covers more than one site" (DOC-04). A decision, not a setting. */
.check {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 2px 0 10px;
  font-size: var(--t-dense);
  color: var(--ink);
  cursor: pointer;
}

/* ------------------------------------------------------------------------------- small print */

/* Rank three: the caveat. Muted, 12px, a measure capped so it does not run the width of a wide
   screen. It is still on screen - the spec ranks honesty copy, it does not delete it. */
.note {
  margin: 0 0 8px;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
}

.note strong {
  color: var(--ink-2);
}

.error {
  grid-column: 1 / -1;
  margin: 0;
  padding: 8px 12px;
  font-size: var(--t-dense);
  color: var(--crit);
  background: var(--crit-w);
  border: 1px solid var(--crit-line);
  border-radius: 6px;
}

/* An in-panel region that stops the flow for a moment: a duplicate upload, an interrupted send. */
.reader {
  display: grid;
  gap: 8px;
  justify-items: start;
  padding: 10px 12px;
  margin-top: 8px;
  background: var(--panel-2);
  border: 1px solid var(--line-2);
  border-radius: 6px;
}

.reader .note {
  margin-bottom: 0;
}

/* "Read in part": the document stated something more than once and one of them was read (DOC-07).
   A warning about the figure's completeness, not an error about its correctness. */
.caveat {
  margin: 0;
  padding: 8px 13px;
  max-width: none;
  border-bottom: 1px solid var(--warn-line);
  background: var(--warn-w);
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.5;
}

.caveatTitle {
  font-weight: 700;
  color: var(--warn);
  display: block;
  margin-bottom: 2px;
}

.rule {
  height: 1px;
  margin: 12px 0;
  background: var(--line);
  border: 0;
}

/* ------------------------------------------------------------------------ library controls */

/* The search and the state chips, on one line that wraps. The search takes what it needs and the
   chips take the rest. */
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 16px;
  padding: 12px 13px 0;
}

.search {
  flex: 1 1 260px;
  max-width: 420px;
  margin-bottom: 0;
}

/* The questions only the database can answer (gate 18): a kind, a period, whether a figure cites
   the document. A row of native controls that wraps, never a second page of settings. */
.narrow {
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 100%;
  align-items: flex-end;
  gap: 4px 16px;
}

.narrow .field {
  margin-bottom: 0;
}

.narrowCheck {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  align-self: flex-end;
  padding-bottom: 7px;
  font-size: var(--t-dense);
  color: var(--ink-2);
}

/* The count and the order, under the controls they describe. Both are `<p>`s with test ids. */
.toolbarNotes {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 14px;
  padding: 8px 13px 10px;
  border-bottom: 1px solid var(--line);
}

.toolbarNotes .note {
  margin: 0;
}

/* The state filter: chips, because it narrows one list and does not switch panels. The one in force
   is filled, with a word and a weight as well as a colour, and `aria-pressed` says it to a screen
   reader. */
.filters {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.filter {
  display: inline-flex;
  gap: 6px;
  align-items: baseline;
  padding: 4px 11px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 500;
  line-height: 18px;
  color: var(--ink-2);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition:
    background-color var(--dur-1) var(--ease-std),
    border-color var(--dur-1) var(--ease-std),
    color var(--dur-1) var(--ease-std);
}

.filter:hover {
  color: var(--ink);
  border-color: var(--ink-2);
}

.filterOn,
.filterOn:hover {
  font-weight: 600;
  color: var(--accent);
  background: var(--accent-w);
  border-color: var(--accent);
}

.filterCount {
  font-size: var(--t-min);
  font-weight: 400;
  color: var(--muted);
  font-variant-numeric: lining-nums tabular-nums;
}

/* Table | Grid, in the panel's header. A segmented control: two buttons sharing one outline. */
.viewToggle {
  display: inline-flex;
  flex-shrink: 0;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  overflow: hidden;
}

.viewButton {
  display: inline-flex;
  gap: 5px;
  align-items: center;
  padding: 3px 10px;
  font: inherit;
  font-size: var(--t-min);
  font-weight: 500;
  color: var(--ink-2);
  background: var(--panel);
  border: 0;
  cursor: pointer;
}

.viewButton + .viewButton {
  border-left: 1px solid var(--line-2);
}

.viewButton:hover {
  color: var(--ink);
  background: var(--panel-2);
}

.viewOn,
.viewOn:hover {
  font-weight: 600;
  color: var(--accent);
  background: var(--accent-w);
}

/* ------------------------------------------------------------------------- the grid layout */

/* The library as cards. Each tile is the same facts as a table row, stacked, and the grid is as many
   columns as fit: a tile never gets narrower than the refusal sentence needs to be readable. */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--gap);
  margin: 0;
  padding: 12px 13px 13px;
  list-style: none;
}

.tile {
  display: grid;
  gap: 4px;
  align-content: start;
  height: 100%;
  padding: 12px 13px;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  transition: border-color var(--dur-1) var(--ease-std);
}

.tile:hover {
  border-color: var(--line-2);
}

.tile a[data-testid="record-link"] {
  font-size: var(--t-min);
  color: var(--muted);
  text-decoration: none;
}

.tile a[data-testid="record-link"]:hover {
  color: var(--accent);
  text-decoration: underline;
}

.tileState {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  align-items: center;
  margin-top: 2px;
}

.tileActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.tile .secondary {
  padding: 3px 10px;
}

/* A queue panel is a list that may be long, and a half-width panel that grows with it pushes the
   rest of the screen away. So it stops at a height and scrolls inside itself. Focusable by the
   buttons within it; the library below lists everything, uncapped. */
.queue {
  max-height: 22rem;
  overflow-y: auto;
}

/* --------------------------------------------------------------------------- the drop target */

/* The file input, in a target. Dashed because it is a place to put something; it fills with the
   accent wash while a file is held over it. The native input stays visible and is the control. */
.kindField {
  max-width: 24rem;
}

/* v5 (stage F2): the drop zone is the screen. Taller, quieter, one accent. */
.uploadContext {
  display: flex;
  flex-wrap: wrap;
  gap: 0 16px;
}

.uploadContext .field {
  flex: 1 1 14rem;
  max-width: 24rem;
}

.fieldError {
  font-size: var(--t-min);
  font-weight: 500;
  color: var(--crit);
}

.dropzone {
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 4px;
  min-height: 210px;
  padding: 28px 20px;
  text-align: center;
  background: var(--panel-2);
  border: 1.5px dashed var(--line-2);
  border-radius: var(--radius-lg);
  transition:
    background-color var(--dur-2) var(--ease-std),
    border-color var(--dur-2) var(--ease-std),
    transform var(--dur-2) var(--ease-out);
}

.dropzoneOver {
  background: var(--accent-w);
  border-color: var(--accent);
  transform: scale(1.005);
}

.typeChips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.typeChips li {
  padding: 1px 10px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  background: var(--panel);
  font-size: var(--t-min);
  color: var(--ink-2);
}

@media (prefers-reduced-motion: reduce) {
  .dropzone {
    transition: none;
  }

  .dropzoneOver {
    transform: none;
  }
}

.dropIcon {
  color: var(--accent);
}

.dropTitle {
  margin: 4px 0 0;
  font-size: var(--t-body);
  font-weight: 600;
  color: var(--ink);
}

.dropSub {
  margin: 0 0 6px;
  font-size: var(--t-min);
  color: var(--muted);
}

.dropPick {
  max-width: 100%;
  font: inherit;
  font-size: var(--t-min);
  color: var(--muted);
}

.dropPick::file-selector-button {
  margin-right: 10px;
  padding: 5px 12px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--on-fill);
  background: var(--accent);
  border: 1px solid var(--accent);
  border-radius: var(--radius-ctl);
  cursor: pointer;
}

.dropPick:hover::file-selector-button {
  background: var(--accent-h);
  border-color: var(--accent-h);
}

.dropPick:disabled::file-selector-button {
  color: var(--disabled);
  background: var(--panel-3);
  border-color: var(--line);
  cursor: not-allowed;
}

.truncated {
  margin: 10px 13px 0;
}

.moreRow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* The library while it loads: rows shaped like the result, not a spinner. */
.skeleton {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.skeletonRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 124px 104px;
  gap: 14px;
  padding: 11px 13px;
  border-top: 1px solid var(--line);
}

.skeletonBar {
  height: 10px;
  background: var(--panel-3);
  border-radius: 5px;
  animation:
    evidence-appear 1ms linear 150ms backwards,
    evidence-pulse 1.6s ease-in-out 150ms infinite;
}

.skeletonBar:nth-child(1) {
  width: 62%;
}

@keyframes evidence-appear {
  from {
    opacity: 0;
  }
}

@keyframes evidence-pulse {
  50% {
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeletonBar {
    animation: none;
  }
}

/* ----------------------------------------------------------------------- the review fields */

/* The extracted fields: label, value, confidence, and what can be done. A table in effect, with the
   value as the figure (full ink, tabular) and the label beneath it in rank. A flagged row has a
   rule down its left edge and a wash; the number is what a reviewer reasons about, the colour only
   says where to look. */
.fields {
  display: grid;
  margin: 0;
  padding: 0;
  list-style: none;
}

.field_row {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr) 52px auto;
  gap: 3px 12px;
  align-items: baseline;
  padding: 8px 13px;
  background: transparent;
  border-top: 1px solid var(--line);
  box-shadow: inset 3px 0 0 transparent;
}

.field_row:first-child {
  border-top: 0;
}

.flagged {
  background: var(--warn-w);
  box-shadow: inset 3px 0 0 var(--warn);
}

.gap {
  background: var(--crit-w);
  box-shadow: inset 3px 0 0 var(--crit);
}

.fieldName {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
}

.fieldValue {
  font-size: var(--t-body);
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: lining-nums tabular-nums;
  overflow-wrap: anywhere;
}

.fieldValue em {
  font-style: normal;
  font-weight: 400;
  color: var(--crit);
}

.unit {
  font-size: var(--t-min);
  font-weight: 400;
  color: var(--muted);
}

.confidence {
  font-size: var(--t-min);
  color: var(--muted);
  font-variant-numeric: lining-nums tabular-nums;
  text-align: right;
}

.flagged .confidence {
  font-weight: 600;
  color: var(--warn);
}

.rowActions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* What a person changed, why, and the "stated more than once" caveat: all under the value, aligned
   with it, small print. */
.corrected {
  grid-column: 2 / -1;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.fixForm {
  grid-column: 2 / -1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 6px 8px;
  margin-top: 4px;
  padding: 8px;
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
}

.fixInput {
  min-width: 0;
  padding: 5px 8px;
  font: inherit;
  font-size: var(--t-dense);
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
}

.fixInput:focus-visible {
  border-color: var(--focus);
  outline: 2px solid var(--focus);
  outline-offset: 0;
}

.fixButtons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.fixForm .fixButtons {
  grid-column: 1 / -1;
}

/* The document the review is about, as one strip across the top: name, kind, period, state. */
.docStrip {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
  align-items: center;
  padding: 9px 13px;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
}

.stripItem {
  display: grid;
  gap: 1px;
  min-width: 0;
}

.stripLabel {
  font-size: var(--t-min);
  color: var(--muted);
}

.stripValue {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: lining-nums tabular-nums;
  overflow-wrap: anywhere;
}

.stripMono {
  font-family: var(--mono);
  font-weight: 400;
  color: var(--ink-2);
}

/* The provenance of a reading: a small dl, label over value, in as many columns as fit. */
.provenance {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px 16px;
  margin: 0 0 8px;
}

.provenance dt {
  font-size: var(--t-min);
  font-weight: 500;
  color: var(--muted);
}

.provenance dd {
  margin: 2px 0 0;
  font-size: var(--t-dense);
  color: var(--ink);
  overflow-wrap: anywhere;
}

/* ------------------------------------------------------------------------- review decisions */

/* The reviewable replacement decision (EXP-31, COL-05). Bordered rather than inline, because it is
   a question that stops the flow: a reviewer must not be able to skim past it. */
.duplicate {
  margin-top: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  border: 1px solid var(--warn-line);
  border-left: 3px solid var(--warn);
  background: var(--warn-w);
  border-radius: 6px;
}

.duplicate .note {
  margin: 0;
}

.duplicateHead {
  margin: 0;
  font-size: var(--t-dense);
  font-weight: 650;
  color: var(--warn);
}

.choice {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  font-size: var(--t-dense);
  line-height: 1.45;
  color: var(--ink);
  cursor: pointer;
}

/* Where the competing record came from. Its own line and quieter than the figure, because it is
   what distinguishes two candidates rather than what identifies either (DOC-05). */
.choiceSource {
  display: block;
  margin-top: 1px;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.choice input {
  margin-top: 2px;
  flex: none;
}

/* Why a document became the activity it did (INT-02). A list rather than a sentence: the decisive
   facts are separate, and a reviewer disagrees with one of them at a time. */
.reasons {
  margin: 6px 0 10px;
  padding-left: 16px;
  max-width: 92ch;
  font-size: var(--t-dense);
  line-height: 1.55;
  color: var(--ink-2);
}

.reasons li {
  margin-bottom: 2px;
}

/* "What this became" - one activity record and the figures priced from it (EXP-19). */
.usageRow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 13px;
  border-top: 1px solid var(--line);
  font-size: var(--t-dense);
}

.usageRow:first-child {
  border-top: 0;
}

.usageMain {
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-weight: 600;
  color: var(--ink);
}

.usageSub {
  font-size: var(--t-min);
  font-weight: 400;
  color: var(--muted);
  font-variant-numeric: lining-nums tabular-nums;
}

.usageFigures {
  display: flex;
  gap: 14px;
  flex-shrink: 0;
  font-variant-numeric: lining-nums tabular-nums;
}

/* ------------------------------------------------------------------------- queue summaries */

/* The groups "Where you were" reports: a title, what moves them on, and the one control that does.
   They are rows, like a document is. */
.verdict {
  margin: 0;
  max-width: none;
  font-size: var(--t-dense);
  line-height: 1.5;
  color: var(--ink-2);
}

.verdict strong {
  color: var(--ink);
}

/* ------------------------------------------------------------------- the review: two panes */

/* The document on the left and what was read from it on the right (journey 2). The left pane is
   `position: sticky`, so while the reviewer works down a long list of fields and the form beneath
   it the text they are checking against stays in view. It spans the two right-hand panels, and it
   is bounded by the viewport so that it never makes those rows taller than they need to be: its
   own body scrolls instead.

   It is a wrapper `div` and not the panel because a sticky element needs a box of its own to stick
   within, and `SourceTextPane` returns a `Panel` whose placement is a custom property. */
.reviewDoc {
  grid-column: 1 / 7;
  grid-row: span 2;
  align-self: start;
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  min-width: 0;
  max-height: calc(100vh - var(--top) - 4 * var(--gap));
}

.reviewDoc > section {
  flex: 1;
  min-width: 0;
}

/* The text itself takes the height the pane has, instead of the fixed 22rem it has elsewhere: that
   is the document, and the document is the point of this screen. The selector is the pane's own
   test id, which its stylesheet's hashed class cannot be reached by from here. */
.reviewDoc [data-testid="source-body"] {
  max-height: calc(100vh - 26rem);
  min-height: 14rem;
}

/* At phone width, and on a short screen, the panes stack in reading order and nothing sticks: a
   sticky box taller than the viewport is a box whose bottom can never be reached. */
@media (max-width: 900px) {
  .reviewDoc {
    grid-column: 1 / -1;
    grid-row: auto;
    position: static;
    max-height: none;
  }

  .reviewDoc [data-testid="source-body"] {
    max-height: 22rem;
  }
}

@media (max-height: 620px) {
  .reviewDoc {
    position: static;
    max-height: none;
  }
}

/* The column labels over the field list. Decorative repetition of what each row already says in
   words, so it is hidden from assistive technology; it shares the row's template so the labels sit
   over their columns. */
.fieldsHead {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr) 52px auto;
  gap: 3px 12px;
  padding: 6px 13px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
  background: var(--panel-2);
  border-bottom: 1px solid var(--line);
}

.fieldsHead > :nth-child(3) {
  text-align: right;
}

/* ---------------------------------------------------------------------- phone: a row stacks */

/* At phone width a document row has no room for its name, its state and its buttons side by side:
   the name was cut to six letters by the pill and the button. So the row is one column - the name,
   then the facts, then what can be done - in the order a reader takes them in. It comes last in the
   file so it outranks the narrower rules above it. */
@media (max-width: 600px) {
  .card {
    grid-template-columns: minmax(0, 1fr);
  }

  .cardSide {
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .tableRow .cells {
    grid-column: 1;
    grid-row: 2;
  }

  .tableRow .cardSide {
    grid-column: 1;
    grid-row: 3;
    align-self: start;
  }

  /* And the review's field list gives its name column less, so the value and its confidence have the
     width a reading needs. Both rows share the template so the labels stay over their columns. */
  .field_row,
  .fieldsHead {
    grid-template-columns: 96px minmax(0, 1fr) 44px auto;
  }
}
